import { useState, memo, useEffect, useMemo } from '@wordpress/element';
import { Badge, Select } from '@bsf/force-ui';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { __, sprintf } from '@wordpress/i18n';
import {
	formatDate,
	parseHeaders,
	getStatusLabel,
	getStatusVariant,
	ShadowDOM,
	stringToHtml,
	containsHtmlTag,
} from '@utils/utils';

const DrawerLogBody = ( { log } ) => {
	const [ isBodyOpen, setBodyOpen ] = useState( true );
	const [ isServerResponseOpen, setServerResponseOpen ] = useState( true );
	const [ isEmailHeaderOpen, setEmailHeaderOpen ] = useState( true );
	const [ isEmailAttachmentsOpen, setEmailAttachmentsOpen ] =
		useState( false );
	const [ selectedRetry, setSelectedRetry ] = useState( null );
	const [ groupedResponses, setGroupedResponses ] = useState( {} );

	const attachments = Array.isArray( log.attachments ) ? log.attachments : [];

	useEffect( () => {
		if ( attachments.length > 0 ) {
			setEmailAttachmentsOpen( true );
		}
	}, [ attachments.length ] );

	// Parse serialized JSON or comma-separated values in email fields
	const parseEmailField = ( field ) => {
		if ( ! field ) {
			return [];
		}
		try {
			// Check if serialized JSON and parse
			return typeof field === 'string' && field.startsWith( '[' )
				? JSON.parse( field )
				: field.split( ',' ).map( ( email ) => email.trim() );
		} catch {
			return field.split( ',' ).map( ( email ) => email.trim() );
		}
	};

	// Function to extract charset from Content-Type
	const extractCharset = ( contentType ) => {
		if ( ! contentType ) {
			return '';
		}
		const match = contentType.match( /charset=([\w-]+)/i );
		return match ? match[ 1 ] : '';
	};

	// Use the utility function to parse headers
	const headers = log.headers ? parseHeaders( log.headers ) : {};

	// Initialize headerFields with default empty arrays or values
	const headerFields = {
		From: headers.From ? headers.From[ 0 ] : '',
		'Reply-To': headers[ 'Reply-To' ] || [],
		CC: headers.Cc || [],
		BCC: headers.Bcc || [],
		'Content-Type': headers[ 'Content-Type' ]
			? headers[ 'Content-Type' ][ 0 ]
			: '',
		Charset: extractCharset(
			headers[ 'Content-Type' ] ? headers[ 'Content-Type' ][ 0 ] : ''
		),
		'X-Mailer': headers[ 'X-Mailer' ] ? headers[ 'X-Mailer' ][ 0 ] : '',
	};

	useEffect( () => {
		const response = log.response || [];
		const grouped = response.reduce( ( acc, res ) => {
			const retryNumber = Number( res.retry );
			if ( ! acc[ retryNumber ] ) {
				acc[ retryNumber ] = [];
			}
			acc[ retryNumber ].push( res );
			return acc;
		}, {} );

		setGroupedResponses( grouped );
	}, [ log.response ] );

	const sortedRetryKeys = useMemo( () => {
		return Object.keys( groupedResponses )
			.map( Number )
			.sort( ( a, b ) => a - b );
	}, [ groupedResponses ] );

	const retries = useMemo( () => {
		return sortedRetryKeys.map( ( retry ) => ( {
			label: `Response ${ retry + 1 }`,
			value: retry,
		} ) );
	}, [ sortedRetryKeys ] );

	const getRetryLabel = ( value ) => {
		const retry = retries.find( ( r ) => r.value === value );
		return retry ? retry.label : '';
	};

	useEffect( () => {
		if ( retries.length > 0 ) {
			const isValidRetry = retries.some(
				( retry ) => retry.value === selectedRetry
			);
			if ( ! isValidRetry ) {
				setSelectedRetry( retries[ retries.length - 1 ].value );
			}
		} else {
			setSelectedRetry( null );
		}
	}, [ retries, selectedRetry ] );

	const handleRetryChange = ( selectedValue ) => {
		setSelectedRetry( selectedValue );
	};

	const createAndAttachEmailBody = ( content ) => ( node ) => {
		if ( ! node || ! content ) {
			return;
		}
		const emailBodyShadow = new ShadowDOM( node );
		if ( emailBodyShadow.hasChildNodes() ) {
			return;
		}
		// Wrapper for adding 8px padding to the email body
		const wrapper = document.createElement( 'div' );
		wrapper.style.padding = containsHtmlTag( content ) ? '0' : '0.5rem';
		// Escape HTML tags and convert plain text to HTML
		wrapper.innerHTML = stringToHtml( content );
		// Append the wrapper to the email body shadow
		emailBodyShadow.appendChild( wrapper );
	};

	return (
		<div className="rounded-lg bg-background-secondary">
			{ /* Email Information Section */ }
			<div className="p-4 m-1 space-y-2 border rounded-md shadow-sm bg-background-primary">
				<div className="flex items-center justify-between">
					<p>
						<strong className="text-sm font-normal text-text-tertiary">
							{ __( 'Sent by:', 'suremails' ) }
						</strong>{ ' ' }
						<strong className="text-sm font-normal text-text-primary">
							{ log.email_from }
						</strong>
					</p>
					<div className="flex items-center space-x-2">
						<Badge
							className="py-0.5"
							label={
								log.connection ||
								__( 'Unknown Provider', 'suremails' )
							}
							variant="blue"
							disableHover
						/>
						<Badge
							className="py-0.5"
							label={ getStatusLabel( log.status ) }
							variant={ getStatusVariant( log.status ) }
							disableHover
						/>
					</div>
				</div>

				{ /* Second Row: Sent to and Date */ }
				<div className="flex items-center justify-between">
					<p>
						<strong className="text-sm font-normal text-text-tertiary">
							{ __( 'Sent to:', 'suremails' ) }
						</strong>{ ' ' }
						<strong className="text-sm font-normal text-text-primary">
							{ parseEmailField( log.email_to ).join( ', ' ) }
						</strong>
					</p>
					<p className="text-sm font-normal text-text-secondary">
						{ formatDate( log.created_at, {
							day: true,
							month: true,
							year: true,
							hour: true,
							minute: true,
							hour12: true,
						} ) }
					</p>
				</div>

				{ /* Third Row: Subject */ }
				<p>
					<strong className="text-sm font-normal text-text-tertiary">
						{ __( 'Subject:', 'suremails' ) }
					</strong>{ ' ' }
					<strong className="text-sm font-normal text-text-primary">
						{ log.subject }
					</strong>
				</p>
				<div className="flex items-start justify-start">
					<p>
						<strong className="text-sm font-normal text-text-tertiary hidden">
							{ __( 'Resent:', 'suremails' ) }
						</strong>{ ' ' }
						<strong className="text-sm font-normal text-text-primary hidden">
							{ log.meta?.resend }
						</strong>
					</p>
					<p>
						<strong className="text-sm font-normal text-text-tertiary">
							{ __( 'Retries:', 'suremails' ) }
						</strong>{ ' ' }
						<strong className="text-sm font-normal text-text-primary">
							{ log.meta?.retry }
						</strong>
					</p>
				</div>
			</div>

			{ /* Email Body Section */ }
			<div className="p-4 m-1 border rounded-md shadow-sm bg-background-primary">
				<div
					className="flex items-center justify-between cursor-pointer"
					onClick={ () => setBodyOpen( ! isBodyOpen ) }
				>
					<h3 className="text-sm font-medium text-field-label">
						{ __( 'Email Body', 'suremails' ) }
					</h3>
					{ isBodyOpen ? (
						<ChevronUp className="size-5" />
					) : (
						<ChevronDown className="size-5" />
					) }
				</div>
				{ isBodyOpen && (
					<div
						ref={ createAndAttachEmailBody( log.body ) }
						className="mt-2 bg-background-secondary rounded overflow-hidden"
					>
						{ log.body
							? false
							: __( 'No email body available.', 'suremails' ) }
					</div>
				) }
			</div>

			{ /* Server Response Section */ }
			<div className="p-4 m-1 border rounded-md shadow-sm bg-background-primary">
				<div
					className="flex items-center justify-between cursor-pointer"
					onClick={ () =>
						setServerResponseOpen( ! isServerResponseOpen )
					}
				>
					<h3 className="text-sm font-medium text-field-label">
						{ __( 'Server Response', 'suremails' ) }
					</h3>
					{ isServerResponseOpen ? (
						<ChevronUp className="size-5" />
					) : (
						<ChevronDown className="size-5" />
					) }
				</div>
				{ isServerResponseOpen && (
					<div className="flex flex-col gap-2 mt-2">
						{ ' ' }
						<Select
							value={ getRetryLabel( selectedRetry ) }
							onChange={ ( value ) => handleRetryChange( value ) }
							className="w-full"
							by={ selectedRetry }
						>
							<Select.Button />
							<Select.Options>
								{ retries.map( ( retry ) => (
									<Select.Option
										key={ retry.value }
										value={ retry.value }
									>
										{ retry.label }
									</Select.Option>
								) ) }
							</Select.Options>
						</Select>
						<div className="flex items-start justify-between gap-2 mt-2 rounded-md bg-background-primary">
							<div className="flex-1">
								{ groupedResponses[ selectedRetry ]?.length >
								0 ? (
									<div className="flex flex-col gap-2">
										{ ' ' }
										{ groupedResponses[ selectedRetry ].map(
											( res, index ) => (
												<div
													key={ index }
													className="py-2 px-2 gap-3 rounded border bg-background-secondary"
												>
													{ /* Message and Connection inline */ }
													<div className="flex flex-col gap-1">
														{ ' ' }
														<p className="font-medium text-sm text-text-primary">
															{ __(
																'Message:',
																'suremails'
															) }{ ' ' }
															<span className="font-normal text-sm text-text-secondary">
																{ res.Message }
															</span>
														</p>
														<p className="font-medium text-sm text-text-primary">
															{ __(
																'Connection:',
																'suremails'
															) }{ ' ' }
															<span className="font-normal text-text-secondary">
																{
																	res.Connection
																}
															</span>
														</p>
													</div>
												</div>
											)
										) }
									</div>
								) : (
									<p className="text-sm text-text-secondary">
										{ __(
											'No server response available.',
											'suremails'
										) }
									</p>
								) }
							</div>
						</div>
					</div>
				) }
			</div>

			{ /* Email Headers Section */ }
			<div className="p-4 m-1 border rounded-md shadow-sm bg-background-primary">
				<div
					className="flex items-center justify-between cursor-pointer"
					onClick={ () => setEmailHeaderOpen( ! isEmailHeaderOpen ) }
				>
					<h3 className="text-sm font-medium text-field-label">
						{ __( 'Email Headers', 'suremails' ) }
					</h3>
					{ isEmailHeaderOpen ? (
						<ChevronUp className="size-5" />
					) : (
						<ChevronDown className="size-5" />
					) }
				</div>
				{ isEmailHeaderOpen && (
					<div className="mt-2 space-y-2">
						<div>
							<strong className="text-sm font-normal text-text-tertiary">
								{ __( 'From:', 'suremails' ) }
							</strong>{ ' ' }
							<strong className="text-sm font-normal text-text-primary">
								{ headerFields.From &&
								headerFields.From.length > 0 ? (
									<span>{ headerFields.From }</span>
								) : (
									''
								) }
							</strong>
						</div>
						<div>
							<strong className="text-sm font-normal text-text-tertiary">
								{ __( 'Reply-To:', 'suremails' ) }
							</strong>{ ' ' }
							<strong className="text-sm font-normal text-text-primary">
								{ headerFields[ 'Reply-To' ] &&
								headerFields[ 'Reply-To' ].length > 0 ? (
									<span>
										{ headerFields[ 'Reply-To' ].join(
											', '
										) }
									</span>
								) : (
									''
								) }
							</strong>
						</div>
						<div>
							<strong className="text-sm font-normal text-text-tertiary">
								{ __( 'CC:', 'suremails' ) }
							</strong>{ ' ' }
							<strong className="text-sm font-normal text-text-primary">
								{ headerFields.CC &&
								headerFields.CC.length > 0 ? (
									<span>
										{ headerFields.CC.join( ', ' ) }
									</span>
								) : (
									''
								) }
							</strong>
						</div>
						<div>
							<strong className="text-sm font-normal text-text-tertiary">
								{ __( 'BCC:', 'suremails' ) }
							</strong>{ ' ' }
							<strong className="text-sm font-normal text-text-primary">
								{ headerFields.BCC &&
								headerFields.BCC.length > 0 ? (
									<span>
										{ headerFields.BCC.join( ', ' ) }
									</span>
								) : (
									''
								) }
							</strong>
						</div>
						<div>
							<strong className="text-sm font-normal text-text-tertiary">
								{ __( 'Content-Type:', 'suremails' ) }
							</strong>{ ' ' }
							<strong className="text-sm font-normal text-text-primary">
								{ headerFields[ 'Content-Type' ] || '' }
							</strong>
						</div>
						<div>
							<strong className="text-sm font-normal text-text-tertiary">
								{ __( 'X-Mailer:', 'suremails' ) }
							</strong>{ ' ' }
							<strong className="text-sm font-normal text-text-primary">
								{ headerFields[ 'X-Mailer' ] || '' }
							</strong>
						</div>
					</div>
				) }
			</div>

			{ /* Email Attachments Section */ }
			<div className="p-4 m-1 mb-1 border rounded-md shadow-sm bg-background-primary">
				<div
					className="flex items-center justify-between cursor-pointer"
					onClick={ () =>
						setEmailAttachmentsOpen( ! isEmailAttachmentsOpen )
					}
				>
					<h3 className="text-sm font-medium text-field-label">
						{ sprintf(
							/* translators: %d: Number of attachments. */
							__( 'Attachments (%d)', 'suremails' ),
							attachments.length
						) }
					</h3>
					{ isEmailAttachmentsOpen ? (
						<ChevronUp className="size-5" />
					) : (
						<ChevronDown className="size-5" />
					) }
				</div>
				{ isEmailAttachmentsOpen && (
					<div className="mt-2">
						{ attachments.length > 0 ? (
							<ul className="space-y-1 list-inside">
								{ attachments.map( ( attachment, index ) => (
									<li key={ index }>
										{ attachment ??
											sprintf(
												/* translators: %s: Attachment number. */
												__(
													'Attachment %s',
													'suremails'
												),
												index + 1
											) }
									</li>
								) ) }
							</ul>
						) : (
							<p className="text-sm text-text-secondary">
								{ __( 'No attachments.', 'suremails' ) }
							</p>
						) }
					</div>
				) }
			</div>
		</div>
	);
};

export default memo( DrawerLogBody );
